import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MetaService } from '@rma/generic/util/meta-service';
import { rmaShareReplay } from '@rma/generic/util/operators/combined';
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs';
import { catchError, debounceTime, map, scan, switchMap, withLatestFrom } from 'rxjs/operators';
import { BlogService } from '../data-access-blog/blog.service';
import { BlogList, BlogPost } from '../domain';
import { PostListComponent } from '../ui-post-list/post-list.component';

@UntilDestroy()
@Component({
  selector: 'rma-blog-list',
  templateUrl: './blog-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PostListComponent, AsyncPipe],
})
export class BlogListComponent {
  public readonly isHome$: Observable<boolean>;
  public readonly posts$: Observable<BlogPost[]>;
  public readonly displayShowMore$: Observable<boolean>;

  private currentPage = new BehaviorSubject<number>(1);

  public constructor(
    private readonly blogService: BlogService,
    private readonly metaService: MetaService,
    private readonly translateService: TranslocoService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    const page$ = this.currentPage.asObservable();

    const slug$ = this.route.paramMap.pipe(
      map((params) => (params.get('slug') || '').trim()),
      untilDestroyed(this),
    );

    this.isHome$ = this.route.data.pipe(
      map((data) => !!data['isHome']),
      untilDestroyed(this),
    );

    const response$ = this.isHome$.pipe(
      switchMap((isHome) => (isHome ? this.getAllPosts(page$) : this.getPostByCategory(page$, slug$))),
      rmaShareReplay(),
    );

    this.posts$ = response$.pipe(
      map((result) => result.posts),
      withLatestFrom(page$),
      scan((accum, [results, page]) => (page === 1 ? results : [...accum, ...results]), []),
      rmaShareReplay(),
    );

    this.displayShowMore$ = response$.pipe(
      map((result) => result.totalPages),
      withLatestFrom(page$),
      map(([pageCount, currentPage]) => currentPage < pageCount),
      rmaShareReplay(),
    );

    slug$.subscribe(() => this.currentPage.next(1));

    this.isHome$
      .pipe(
        switchMap((isHome: boolean) =>
          isHome
            ? this.translateService.selectTranslate('country-name').pipe(map((country) => `${country} Real Estate Blog`))
            : response$.pipe(map((result) => result.categoryName)),
        ),
        map((title) => title ?? ''),
        untilDestroyed(this),
      )
      .subscribe((title) => this.metaService.update({ title, canonicalUrl: this.router.url, description: title }));
  }

  public loadMore(): void {
    this.currentPage.next(this.currentPage.value + 1);
  }

  private getAllPosts(page$: Observable<number>): Observable<BlogList> {
    return combineLatest([page$]).pipe(
      switchMap(([page]) => this.blogService.getAllPosts(page)),
      untilDestroyed(this),
      rmaShareReplay(),
    );
  }

  private getPostByCategory(page$: Observable<number>, slug$: Observable<string>): Observable<BlogList> {
    return combineLatest([page$, slug$]).pipe(
      debounceTime(100),
      switchMap(([page, slug]) => this.blogService.getPostsByCategorySlug(slug, page, page === 1 ? 8 : 9)),
      catchError((e) => {
        this.router.navigate(['/blog']);
        return throwError(() => e);
      }),
      untilDestroyed(this),
      rmaShareReplay(),
    );
  }
}
